import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import { toast } from 'react-toastify'

import FilterSearch from 'pages/parts/FilterSearch'
import FilterPagination from 'pages/parts/FilterPagination'

import { formatMoney, returnSign } from 'helpers/helpers'
import { httpOk, GET, POST } from 'helpers/api'

const HARD_MAX_PERCENT = 70

const initialLevels = {
	sub_parent_id: null,
	parent_percent: '',
	sub_parent_percent: '',
	sub_children_percent: '',
}

function Index() {
	const { t } = useTranslation()

	const [partnerOptions, setPartnerOptions] = useState([])
	const [selectedPartner, setSelectedPartner] = useState(null)
	const [treeData, setTreeData] = useState(null)
	const [partnerRows, setPartnerRows] = useState([])
	const [poses, setPoses] = useState({ data: [], total: 0 })
	const [levels, setLevels] = useState(initialLevels)
	const [treePreview, setTreePreview] = useState(null)
	const [loadingTree, setLoadingTree] = useState(false)
	const [loadingPoses, setLoadingPoses] = useState(false)
	const [posSearch, setPosSearch] = useState('')
	const [posPage, setPosPage] = useState(null)
	const [posSize] = useState(20)
	const [posFilterFrom, setPosFilterFrom] = useState('')
	const [posFilterTo, setPosFilterTo] = useState('')
	const [attachMissingOnly, setAttachMissingOnly] = useState(false)

	const rootPartner = treeData?.root || null

	const partnerSelectOptions = useMemo(() => (
		partnerOptions.map((item) => ({
			value: item.id,
			label: `#${item.id} ${item.name || item.login} (${item.login})`,
			item,
		}))
	), [partnerOptions])

	const subParentOptions = useMemo(() => (
		partnerRows
			.filter((row) => row.parent_id === rootPartner?.id)
			.map((row) => ({
				value: row.id,
				label: `#${row.id} ${row.first_name} (${row.login})`,
				item: row,
			}))
	), [partnerRows, rootPartner])

	const subChildren = useMemo(() => {
		if (!levels.sub_parent_id) return []
		return partnerRows.filter((row) => row.parent_id === levels.sub_parent_id)
	}, [partnerRows, levels.sub_parent_id])

	const hasSubChildren = subChildren.length > 0
	const subChildrenCount = subChildren.length

	// «Всем суб-детям %» ставится КАЖДОМУ ребёнку → в сумме умножаем на число детей
	const levelsSum = useMemo(() => (
		Number(levels.parent_percent || 0)
		+ Number(levels.sub_parent_percent || 0)
		+ (subChildrenCount > 0 ? Number(levels.sub_children_percent || 0) * subChildrenCount : 0)
	), [levels, subChildrenCount])

	const levelsValid = levelsSum <= HARD_MAX_PERCENT
		&& !!levels.sub_parent_id
		&& levels.parent_percent !== ''
		&& levels.sub_parent_percent !== ''
		&& (!hasSubChildren || levels.sub_children_percent !== '')

	function getLevelsInvalidMessage() {
		if (!levels.sub_parent_id) return t('select_sub_parent')
		if (levels.parent_percent === '' || levels.sub_parent_percent === '') {
			return t('fill_required_fields') || 'Заполните проценты родителя и суб-родителя'
		}
		if (hasSubChildren && levels.sub_children_percent === '') {
			return t('fill_required_fields') || 'Заполните процент суб-детям'
		}
		if (levelsSum > HARD_MAX_PERCENT) {
			if (subChildrenCount > 1) {
				return `Сумма (родитель + суб-родитель + ${subChildrenCount}×суб-дети) не может превышать ${HARD_MAX_PERCENT}%`
			}
			return t('bonus_sum_exceeds_limit', { max: HARD_MAX_PERCENT })
		}
		return t('bonus_sum_exceeds_limit', { max: HARD_MAX_PERCENT })
	}

	function suggestLevelsFromPosRow(posesData, rootId, subParentId, children) {
		const childIds = (children || []).map((c) => c.id)
		const empty = {
			parent_percent: '',
			sub_parent_percent: '',
			sub_children_percent: childIds.length > 0 ? '' : '0',
		}
		const first = (posesData || []).find((row) => (row.partners || []).length > 0)
		if (!first) return empty

		const lines = first.partners || []
		const parentLine = lines.find((p) => Number(p.partner_id) === Number(rootId))
		const subLine = lines.find((p) => Number(p.partner_id) === Number(subParentId))
		let childPercent = childIds.length > 0 ? '' : '0'
		if (childIds.length > 0) {
			const childLine = lines.find((p) => childIds.includes(Number(p.partner_id)))
			childPercent = childLine != null ? String(childLine.partner_percentage) : '0'
		}

		return {
			parent_percent: parentLine != null ? String(parentLine.partner_percentage) : '',
			sub_parent_percent: subLine != null ? String(subLine.partner_percentage) : '',
			sub_children_percent: childPercent,
		}
	}

	async function loadPartnerOptions() {
		const response = await GET('/admin/helper/partners', {}, { loader: true })
		if (httpOk(response)) {
			setPartnerOptions(response.data || [])
		}
	}

	async function loadTree(partnerId) {
		if (!partnerId) return

		setLoadingTree(true)
		const response = await GET(`/admin/partner-bonus/tree?partner_id=${partnerId}`, {}, { loader: true })
		setLoadingTree(false)

		if (httpOk(response)) {
			const data = response.data
			setTreeData(data)
			const rows = (data?.partners || []).map((item) => ({ ...item }))
			setPartnerRows(rows)
			setTreePreview(null)

			const root = data?.root
			const firstSubParent = rows.find((row) => row.parent_id === root?.id)
			const nextSubParentId = firstSubParent?.id || null
			const firstChildren = nextSubParentId
				? rows.filter((row) => row.parent_id === nextSubParentId)
				: []
			setLevels({
				sub_parent_id: nextSubParentId,
				parent_percent: '',
				sub_parent_percent: '',
				sub_children_percent: firstChildren.length > 0 ? '' : '0',
			})

			// % берём с торговых точек, не из users_partners (карточка агента часто > 70 в сумме).
			const posesData = await loadPoses(partnerId, { sub_parent_id: nextSubParentId })
			if (nextSubParentId && root?.id) {
				const suggested = suggestLevelsFromPosRow(posesData, root.id, nextSubParentId, firstChildren)
				setLevels({
					sub_parent_id: nextSubParentId,
					...suggested,
				})
			}
		}
	}

	async function loadPoses(partnerId, overrides = {}) {
		if (!partnerId) return []

		setLoadingPoses(true)
		let url = `/admin/partner-bonus/poses?partner_id=${partnerId}&size=${posSize}`
		const search = overrides.search ?? posSearch
		const page = overrides.page ?? posPage
		const posIdFrom = overrides.pos_id_from ?? posFilterFrom
		const posIdTo = overrides.pos_id_to ?? posFilterTo
		const subParentId = overrides.sub_parent_id ?? levels.sub_parent_id

		if (subParentId) url += `${returnSign(url)}sub_parent_id=${subParentId}`
		if (search) url += `${returnSign(url)}search=${encodeURIComponent(search)}`
		if (page) url += `${returnSign(url)}page=${page}`
		if (posIdFrom) url += `${returnSign(url)}pos_id_from=${posIdFrom}`
		if (posIdTo) url += `${returnSign(url)}pos_id_to=${posIdTo}`

		const response = await GET(url, {}, { loader: true })
		setLoadingPoses(false)

		if (httpOk(response)) {
			const nextData = response.data?.data || []
			setPoses({
				data: nextData,
				total: response.data?.total || 0,
			})
			return nextData
		}

		return []
	}

	function handlePartnerChange(option) {
		setSelectedPartner(option)
		setPosPage(null)
		setPosSearch('')
		setPosFilterFrom('')
		setPosFilterTo('')
		setTreePreview(null)
		setLevels(initialLevels)
		setAttachMissingOnly(false)

		if (option?.value) {
			loadTree(option.value)
		} else {
			setTreeData(null)
			setPartnerRows([])
			setPoses({ data: [], total: 0 })
		}
	}

	async function handleSubParentChange(option) {
		const nextSubParentId = option?.value || null
		const nextChildren = nextSubParentId
			? partnerRows.filter((row) => row.parent_id === nextSubParentId)
			: []

		setLevels({
			sub_parent_id: nextSubParentId,
			parent_percent: '',
			sub_parent_percent: '',
			sub_children_percent: nextChildren.length > 0 ? '' : '0',
		})
		setTreePreview(null)
		setPosPage(null)

		if (selectedPartner?.value && nextSubParentId) {
			const posesData = await loadPoses(selectedPartner.value, { sub_parent_id: nextSubParentId, page: null })
			const suggested = suggestLevelsFromPosRow(
				posesData,
				rootPartner?.id,
				nextSubParentId,
				nextChildren
			)
			setLevels({
				sub_parent_id: nextSubParentId,
				...suggested,
			})
		}
	}

	function buildLevelsPayload() {
		return {
			partner_id: rootPartner?.id || selectedPartner.value,
			mode: 'levels',
			sub_parent_id: levels.sub_parent_id,
			parent_percent: Number(levels.parent_percent),
			sub_parent_percent: Number(levels.sub_parent_percent),
			sub_children_percent: hasSubChildren ? Number(levels.sub_children_percent || 0) : 0,
			attach_missing_only: attachMissingOnly,
			use_list_filter: true,
			search: posSearch || '',
			list_pos_id_from: posFilterFrom || '',
			list_pos_id_to: posFilterTo || '',
		}
	}

	async function previewLevelsOnPoses() {
		if (!levelsValid) {
			toast.error(getLevelsInvalidMessage())
			return
		}

		const response = await POST('/admin/partner-bonus/poses/tree/preview', buildLevelsPayload(), { loader: true })
		if (httpOk(response)) {
			setTreePreview(response.data)
		}
	}

	async function applyLevelsOnPoses() {
		if (!levelsValid) {
			toast.error(getLevelsInvalidMessage())
			return
		}

		const response = await POST('/admin/partner-bonus/poses/tree', buildLevelsPayload(), { loader: true })
		if (httpOk(response)) {
			toast.success(response.data?.message || t('success'))
			setTreePreview(null)
			loadTree(selectedPartner.value)
		}
	}

	function renderPosStatus(status) {
		if (status === 1) return t('active')
		if (status === -1) return t('not_active')
		if (status === -3) return t('deleted')
		return status
	}

	useEffect(() => {
		loadPartnerOptions()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between flex-wrap gap-2">
				<h4 className="vertical-center mb-0">{t('admin_partner_bonus_distribution')}</h4>
			</div>

			<div className="card mb-3">
				<div className="card-body">
					<div className="row g-3">
						<div className="col-lg-6">
							<label className="form-label">{t('select_parent_partner')}</label>
							<Select
								options={partnerSelectOptions}
								value={selectedPartner}
								onChange={handlePartnerChange}
								isClearable
								isLoading={partnerOptions.length === 0}
								placeholder={t('select_partner')}
								noOptionsMessage={() => t('list_empty')}
							/>
						</div>
						{rootPartner &&
							<div className="col-lg-6">
								<div className="alert alert-light border mb-0 py-2">
									<b>{t('branch_root')}:</b> #{rootPartner.id} {rootPartner.first_name} ({rootPartner.login})
									{' · '}
									{t('max_bonus_limit')}: <b>{HARD_MAX_PERCENT}%</b>
								</div>
							</div>
						}
					</div>
				</div>
			</div>

			{selectedPartner?.value && rootPartner &&
				<div className="row g-3">
					<div className="col-xl-5">
						<div className="card h-100">
							<div className="card-body">
								<h5 className="mb-3">{t('bonus_levels_setup')}</h5>

								<div className="mb-3">
									<label className="form-label">{t('select_sub_parent')}</label>
									<Select
										options={subParentOptions}
										value={subParentOptions.find((item) => item.value === levels.sub_parent_id) || null}
										onChange={handleSubParentChange}
										placeholder={t('select_sub_parent')}
										noOptionsMessage={() => t('list_empty')}
									/>
								</div>

								<div className="row g-3 mb-3">
									<div className="col-md-4">
										<label className="form-label">
											{t('parent_bonus')} %<br />
											<small className="text-muted">#{rootPartner.id} {rootPartner.first_name}</small>
										</label>
										<input
											type="number"
											min="0"
											max={HARD_MAX_PERCENT}
											step="0.01"
											className="form-control"
											value={levels.parent_percent}
											onChange={(e) => setLevels({ ...levels, parent_percent: e.target.value })}
										/>
									</div>
									<div className="col-md-4">
										<label className="form-label">
											{t('sub_parent_bonus')} %<br />
											<small className="text-muted">
												{levels.sub_parent_id
													? `#${levels.sub_parent_id}`
													: t('select_sub_parent')}
											</small>
										</label>
										<input
											type="number"
											min="0"
											max={HARD_MAX_PERCENT}
											step="0.01"
											className="form-control"
											value={levels.sub_parent_percent}
											onChange={(e) => setLevels({ ...levels, sub_parent_percent: e.target.value })}
											disabled={!levels.sub_parent_id}
										/>
									</div>
									<div className="col-md-4">
										<label className="form-label">
											{t('sub_children_bonus')} %<br />
											<small className="text-muted">
												{subChildren.length > 0
													? subChildren.map((c) => `#${c.id}`).join(', ')
													: '—'}
											</small>
										</label>
										<input
											type="number"
											min="0"
											max={HARD_MAX_PERCENT}
											step="0.01"
											className="form-control"
											value={hasSubChildren ? levels.sub_children_percent : '0'}
											onChange={(e) => setLevels({ ...levels, sub_children_percent: e.target.value })}
											disabled={!levels.sub_parent_id || !hasSubChildren}
										/>
									</div>
								</div>

								<div className={`alert py-2 mb-3 ${levelsValid ? 'alert-success' : 'alert-danger'}`}>
									<div className="d-flex justify-content-between">
										<span>{t('levels_sum')}:</span>
										<b>
											{levelsSum.toFixed(2)}% / {HARD_MAX_PERCENT}%
											{subChildrenCount > 1 && levels.sub_children_percent !== '' &&
												<span className="text-muted fw-normal ms-1">
													({Number(levels.parent_percent || 0)}+{Number(levels.sub_parent_percent || 0)}+{subChildrenCount}×{Number(levels.sub_children_percent || 0)})
												</span>
											}
										</b>
									</div>
									{!levelsValid &&
										<div className="small mt-1">{getLevelsInvalidMessage()}</div>
									}
									<div className="small text-muted mt-1">
										Проценты подставляются с торговых точек (не с карточки агента). Сумма на точке ≤ {HARD_MAX_PERCENT}%.
									</div>
								</div>

								<div className="text-muted small mb-3">
									{t('bonus_levels_hint')}
								</div>

								<div className="form-check mb-3">
									<input
										className="form-check-input"
										type="checkbox"
										id="attachMissingOnly"
										checked={attachMissingOnly}
										onChange={(e) => setAttachMissingOnly(e.target.checked)}
									/>
									<label className="form-check-label" htmlFor="attachMissingOnly">
										Только добавить отсутствующих партнёров
										<div className="text-muted small">
											Не меняет уже назначенные % (30%/40% агента останутся).
											Родитель без привязки будет добавлен с указанным % (например 0%).
										</div>
									</label>
								</div>

								<div className="d-flex flex-wrap gap-2">
									<button
										type="button"
										className="btn btn-outline-primary"
										disabled={!levelsValid || loadingTree}
										onClick={previewLevelsOnPoses}>
										{t('preview')}
									</button>
									<button
										type="button"
										className="btn btn-primary"
										disabled={!levelsValid || loadingTree}
										onClick={applyLevelsOnPoses}>
										{attachMissingOnly
											? 'Прикрепить отсутствующих к точкам'
											: t('apply_to_filtered_pos')}
									</button>
								</div>

								{treePreview &&
									<div className="alert alert-warning mt-3 mb-0 py-2 small">
										{t('matched_pos_count', { count: treePreview.matched_count })}
										{treePreview.matched_pos_ids?.length > 0 &&
											<div>ID: {treePreview.matched_pos_ids.join(', ')}</div>
										}
									</div>
								}

								{subChildren.length > 0 &&
									<div className="border-top mt-4 pt-3">
										<div className="text-muted small mb-2">{t('sub_children_list')}:</div>
										{subChildren.map((child) => (
											<div key={child.id} className="small">
												#{child.id} {child.first_name} ({child.login}) — {child.count_pos} {t('pos')}
											</div>
										))}
									</div>
								}
							</div>
						</div>
					</div>

					<div className="col-xl-7">
						<div className="card">
							<div className="card-body">
								<h5 className="mb-3">{t('filtered_pos_list')}</h5>

								<div className="d-flex flex-wrap gap-2 mb-3 align-items-end">
									<div style={{ minWidth: '220px' }}>
										<FilterSearch
											value={posSearch}
											onSearchChange={(search) => {
												setPosSearch(search)
												setPosPage(null)
												loadPoses(selectedPartner.value, { search, page: null })
											}}
										/>
									</div>
									<div>
										<label className="form-label mb-1">{t('pos_id_from')}</label>
										<input
											type="number"
											className="form-control form-control-sm"
											value={posFilterFrom}
											onChange={(e) => setPosFilterFrom(e.target.value)}
										/>
									</div>
									<div>
										<label className="form-label mb-1">{t('pos_id_to')}</label>
										<input
											type="number"
											className="form-control form-control-sm"
											value={posFilterTo}
											onChange={(e) => setPosFilterTo(e.target.value)}
											placeholder="559"
										/>
									</div>
									<button
										type="button"
										className="btn btn-outline-primary btn-sm"
										onClick={() => {
											setPosPage(null)
											setTreePreview(null)
											loadPoses(selectedPartner.value, {
												page: null,
												pos_id_from: posFilterFrom,
												pos_id_to: posFilterTo,
												search: posSearch,
												sub_parent_id: levels.sub_parent_id,
											})
										}}>
										{t('apply')}
									</button>
								</div>

								<div className="text-muted small mb-2">
									{t('filtered_pos_hint_sub_parent', { id: levels.sub_parent_id || '—' })} [{poses.total}]
								</div>

								<div className="table-responsive">
									<table className="table table-bordered mb-0">
										<thead>
											<tr>
												<th>ID</th>
												<th>{t('name')}</th>
												<th>{t('login')}</th>
												<th>{t('status')}</th>
												<th className="text-end">{t('tariff')}</th>
												<th>{t('partner_bonuses')}</th>
												<th className="text-end">{t('total')} %</th>
											</tr>
										</thead>
										<tbody>
											{loadingPoses &&
												<tr>
													<td colSpan="7" className="text-center py-4">{t('loading')}...</td>
												</tr>
											}
											{!loadingPoses && poses.data.length === 0 &&
												<tr>
													<td colSpan="7" className="text-center py-4">{t('list_empty')}</td>
												</tr>
											}
											{!loadingPoses && poses.data.map((row) => (
												<tr key={row.pos_id}>
													<td>{row.pos_id}</td>
													<td>{row.pos_name}</td>
													<td>{row.pos_login}</td>
													<td>{renderPosStatus(row.status)}</td>
													<td className="text-end">{formatMoney(row.tariff)}</td>
													<td>
														{(row.partners || []).map((partner) => (
															<div key={`${row.pos_id}-${partner.partner_id}`} className="small">
																#{partner.partner_id} {partner.partner_name}: <b>{partner.partner_percentage}%</b>
															</div>
														))}
													</td>
													<td className={`text-end ${Number(row.total_partner_percentage) > HARD_MAX_PERCENT ? 'text-danger fw-bold' : ''}`}>
														{row.total_partner_percentage}%
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>

								<FilterPagination
									pagination={{
										url: '',
										size: posSize,
										response: {
											total: poses.total,
											data: poses.data,
										},
									}}
									paginate={(page) => {
										setPosPage(page)
										loadPoses(selectedPartner.value, { page })
									}}
								/>
							</div>
						</div>
					</div>
				</div>
			}
		</>
	)
}

export default Index
